import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { siteApi } from "src/main/api";
import { DeleteShippingAddressResponse } from "src/main/api/site/User";
import { Button } from "src/main/components";
import { ShippingAddressModel } from "src/main/types";
import { handleApiResponse } from "src/main/utils";

interface Props {
  show?: boolean;
  onClose: () => void;
  address: ShippingAddressModel | undefined;
}

export default function ConfirmDelete({ show = false, onClose, address }: Props) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [deleteShippingAddress, { isLoading }] = siteApi.useDeleteShippingAddressMutation();

  const handleClose = () => {
    onClose();
  };

  const handleDelete = async () => {
    if (!address) return;

    const result = await deleteShippingAddress({ addressId: address.id });
    const { data, error } = handleApiResponse<DeleteShippingAddressResponse>(result);

    if (data) {
      toast.success(t("The address has been deleted"));
      navigate("/manage-address");
    }

    if (error) {
      let errorMessage = error?.data?.error?.message ?? t("Cannot delete the address, please try again later");
      toast.error(errorMessage);
    }
  };

  return (
    <Transition.Root
      show={show}
      as={Fragment}
    >
      <Dialog
        as="div"
        className="relative z-50"
        onClose={() => handleClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 z-50 bg-black/90" />
        </Transition.Child>

        <div className="fixed inset-0 z-50 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden text-white transition-all sm:my-8 sm:w-full sm:max-w-sm md:w-fit">
                <div
                  className="flex flex-col items-start justify-start gap-6 rounded-lg border border-purple-300 border-opacity-30 p-6 text-base shadow-inner"
                  style={{
                    background:
                      "radial-gradient(47.89% 60.54% at 51.25% 4.01%, rgba(38, 18, 92, 0.90) 0%, rgba(23, 11, 55, 0.90) 100%)",
                  }}
                >
                  <div className="text-xl font-bold">{t("Confirm Delete")}</div>
                  <div className="text-left">
                    {t("Are you sure want to delete this address?")}
                    <p className="mt-3 text-[#EA3E93]">{t("Kindly recheck your default address after deletion.")}</p>
                  </div>
                  <div className="flex w-full items-center justify-center gap-4">
                    <Button
                      size="md"
                      outlined
                      onClick={handleClose}
                      disabled={isLoading}
                      variant="blue-violet"
                    >
                      {t("Cancel")}
                    </Button>
                    <Button
                      size="md"
                      onClick={handleDelete}
                      loading={isLoading}
                      variant="blue-violet"
                    >
                      {t("Confirm Delete")}
                    </Button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
